// app/src/main.jsx — PWA entry point.
// Provides the CrewNav context, listens for hashchange, gates on auth, loads the
// signed-in user's profile + workplaces from the API, and renders the active
// route (or onboarding when the user has no workplace yet). Server-authoritative:
// screens fetch from the API; there is no local-books rehydrate.

function CrewApp() {
  const [route, setRoute] = React.useState(() => readRoute());
  // null = checking, false = signed out, true = signed in.
  const [authed, setAuthed] = React.useState(null);
  // null = not loaded yet; otherwise { profile, workplaces }.
  const [me, setMe] = React.useState(null);

  const hasOauthReturn = (() => {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.has('code') || url.searchParams.has('error');
    } catch { return false; }
  })();

  // A pending invite (captured at boot before the Cognito redirect stripped the
  // hash) takes priority: accept it regardless of how many workplaces you have.
  const pendingInvite = (() => { try { return sessionStorage.getItem('crew.invite'); } catch { return null; } })();

  React.useEffect(() => {
    const onHash = () => setRoute(readRoute());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Resolve auth state on mount + when the tab regains focus.
  React.useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const claims = window.crew ? await window.crew.auth.currentClaims().catch(() => null) : null;
        if (!cancelled) setAuthed(!!claims);
      } catch {
        if (!cancelled) setAuthed(false);
      }
    };
    check();
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelled = true; document.removeEventListener('visibilitychange', onVis); };
  }, []);

  // Once signed in, load /me (profile + workplaces) and pick an active workplace.
  React.useEffect(() => {
    if (authed !== true || me !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await window.crew.api('/me');
        window.crew.state.me = res;
        window.crew.state.activeWorkplaceId = (res.workplaces[0] && res.workplaces[0].workplaceId) || null;
        // Apply the signed-in member's preferred language to the UI chrome. The
        // profile is the source of truth — without this the app stays in the
        // device/localStorage language even though the user picked, e.g., Thai.
        const prefLang = (res.workplaces[0] && res.workplaces[0].preferredLang)
          || (res.profile && res.profile.preferredLang);
        if (prefLang) window.setLang(prefLang);
        if (!cancelled) setMe(res);
        maybeRegisterPush(); // best-effort Web Push (no-op unless configured + permission granted)
      } catch (e) {
        console.warn('[me] load failed', e && e.message);
        if (!cancelled) setMe({ profile: null, workplaces: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [authed, me]);

  const navValue = React.useMemo(() => ({
    pwa: true,
    current: route,
    navigate: (path) => {
      if (path.startsWith('#')) path = path.slice(1);
      window.location.hash = path;
    },
    back: () => {
      if (window.history.length > 1) window.history.back();
      else window.location.hash = '/board';
    },
  }), [route]);

  const onJoinRoute = route.split('?')[0] === '/join';

  let body;
  if (authed === null) {
    body = <div className="crew" style={{ height: '100%' }} />;
  } else if (hasOauthReturn || !authed) {
    body = <AuthScreen />;
  } else if (pendingInvite || onJoinRoute) {
    body = <Join code={pendingInvite || undefined} />; // accept invite (any workplace count)
  } else if (me === null) {
    body = <div className="crew" style={{ height: '100%' }} />; // loading /me
  } else if (!me.workplaces || me.workplaces.length === 0) {
    body = <Onboard />; // signed in, no workplace yet
  } else {
    body = <Router route={route} />;
  }

  return <CrewNav.Provider value={navValue}>{body}</CrewNav.Provider>;
}

function readRoute() {
  const h = window.location.hash.replace(/^#/, '') || '/board';
  return h.startsWith('/') ? h : '/' + h;
}

// ── Web Push registration (best-effort) ──
// Subscribes this device only when VAPID is configured AND the user has already
// granted notification permission — we never auto-prompt on load. An in-app
// "Enable notifications" toggle can request permission later.
async function maybeRegisterPush() {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    const cfg = window.crew.getConfig();
    if (!cfg.vapidPublicKey || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8(cfg.vapidPublicKey) });
    await window.crew.api('/me/devices', { method: 'POST', body: { subscription: sub.toJSON(), platform: 'web' } });
  } catch (e) { console.warn('[push] register failed', e && e.message); }
}
function urlB64ToUint8(b64) {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

// ── Capture a pending invite BEFORE first render / any OAuth redirect ──
// Cognito returns to the bare redirect URI (hash dropped), so stash the invite
// code from #/join?code=XXX into sessionStorage; CrewApp accepts it post-login.
try {
  const m = (window.location.hash || '').match(/[?&]code=([^&]+)/);
  if ((window.location.hash || '').indexOf('join') !== -1 && m) {
    sessionStorage.setItem('crew.invite', decodeURIComponent(m[1]));
  }
} catch { /* non-fatal */ }

// ── Mount ───────────────────────────────────────────────────
ReactDOM.createRoot(document.getElementById('root')).render(<CrewApp />);

// ── Service worker registration (deferred-activation handshake) ──
if ('serviceWorker' in navigator) {
  let waitingWorker = null;
  const askWaitingToActivate = () => { if (waitingWorker) waitingWorker.postMessage('SKIP_WAITING'); };
  window.addEventListener('pagehide', askWaitingToActivate);
  window.addEventListener('beforeunload', askWaitingToActivate);

  let reloadingForNewSw = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloadingForNewSw) return;
    reloadingForNewSw = true;
    window.location.reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('./sw.js').then((reg) => {
      if (reg.waiting && navigator.serviceWorker.controller) waitingWorker = reg.waiting;
      reg.addEventListener('updatefound', () => {
        const sw = reg.installing;
        if (!sw) return;
        sw.addEventListener('statechange', () => {
          if (sw.state === 'installed' && navigator.serviceWorker.controller) waitingWorker = sw;
        });
      });
    }).catch((err) => console.warn('[Crew] SW registration failed:', err));
  });
}
